import React, { useState, useEffect, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { formatPhone } from '../../utilities/formatPhone';
import { PORTAL_SESSION_KEY } from '../AccountModal';

const functions = getFunctions();
const updatePortalAccount    = httpsCallable(functions, 'updatePortalAccount');
const getEmployeePayRates    = httpsCallable(functions, 'getEmployeePayRates');
const sendPasswordChangeCode = httpsCallable(functions, 'sendPasswordChangeCode');
const confirmPasswordChange  = httpsCallable(functions, 'confirmPasswordChange');

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

/**
 * AccountSettings — self-service profile editor for any portal role.
 * Editable: email, username, phone, address, emergency contact, date of birth, password.
 * Locked: firstName, lastName, payType, hourlyRate, annualSalary (owner-only changes).
 * Pay history: read-only rate history loaded from payRates subcollection.
 */
const AccountSettings = ({ user, onDirtyChange, onUserUpdate }) => {
  const [form, setForm]         = useState(buildForm(user));
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState('');

  // ── POS PIN state ──────────────────────────────────────────────────────────
  const [pinRevealed,   setPinRevealed]   = useState(false);
  const [pinEditing,    setPinEditing]    = useState(false);
  const [pinNew,        setPinNew]        = useState('');
  const [pinConfirm,    setPinConfirm]    = useState('');
  const [pinSaving,     setPinSaving]     = useState(false);
  const [pinError,      setPinError]      = useState('');
  const [pinSaved,      setPinSaved]      = useState(false);
  // Track current PIN in component state so it updates after a save without needing a page reload
  const [currentPin,    setCurrentPin]    = useState(user.posPin || '');

  // ── Password change multi-step state ────────────────────────────────────────
  // pwStep: 'idle' → 'enter' → 'sent' → 'done'
  const [pwStep,      setPwStep]      = useState('idle');
  const [pwForm,      setPwForm]      = useState({ current: '', next: '', confirm: '' });
  const [pwChannel,   setPwChannel]   = useState('email'); // 'email' | 'sms'
  const [pwCode,      setPwCode]      = useState('');
  const [pwMasked,    setPwMasked]    = useState('');
  const [pwSending,   setPwSending]   = useState(false);
  const [pwVerifying, setPwVerifying] = useState(false);
  const [pwError,     setPwError]     = useState('');
  const [dirty, setDirty]       = useState(false);
  const saveFnRef = useRef(null);

  useEffect(() => { setForm(buildForm(user)); }, [user]);

  // Notify parent of clean state whenever form resets
  useEffect(() => {
    if (!dirty) onDirtyChange?.(false, null);
  }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  const markDirty = () => {
    if (!dirty) {
      setDirty(true);
      onDirtyChange?.(true, () => saveFnRef.current?.());
    }
  };

  const set = (field, value) => { setForm(f => ({ ...f, [field]: value })); markDirty(); };
  const setAddr = (field, value) => { setForm(f => ({ ...f, address: { ...f.address, [field]: value } })); markDirty(); };
  const setEmerg = (field, value) => { setForm(f => ({ ...f, emergencyContact: { ...f.emergencyContact, [field]: value } })); markDirty(); };
  const setPw = (field, value) => setPwForm(f => ({ ...f, [field]: value }));

  const pinReset = () => {
    setPinEditing(false);
    setPinNew('');
    setPinConfirm('');
    setPinError('');
  };

  const handlePinSave = async () => {
    setPinError('');
    if (!/^\d{4}$/.test(pinNew))      return setPinError('PIN must be exactly 4 digits.');
    if (pinNew !== pinConfirm)         return setPinError('PINs do not match.');
    setPinSaving(true);
    try {
      await updatePortalAccount({ accountId: user.accountId, updates: { posPin: pinNew } });
      setCurrentPin(pinNew);
      setPinSaved(true);
      pinReset();
      setTimeout(() => setPinSaved(false), 3000);
      // Update parent state + localStorage so the incomplete-profile banner clears immediately
      onUserUpdate?.({ posPin: pinNew });
    } catch (err) {
      setPinError(err.message || 'Failed to update PIN. Try again.');
    } finally {
      setPinSaving(false);
    }
  };

  const pwReset = () => {
    setPwStep('idle');
    setPwForm({ current: '', next: '', confirm: '' });
    setPwCode('');
    setPwMasked('');
    setPwError('');
  };

  const handleSendCode = async () => {
    setPwError('');
    if (!pwForm.current)                           return setPwError('Current password is required.');
    if (!pwForm.next)                              return setPwError('New password is required.');
    if (pwForm.next.length < 8)                    return setPwError('New password must be at least 8 characters.');
    if (pwForm.next !== pwForm.confirm)             return setPwError('New passwords do not match.');
    if (pwChannel === 'sms' && !user.phone)        return setPwError('No phone number on file. Please add one in Contact Info first.');
    if (pwChannel === 'email' && !user.email)      return setPwError('No email on file. Please add one in Contact Info first.');
    setPwSending(true);
    try {
      const res = await sendPasswordChangeCode({
        accountId:       user.accountId,
        currentPassword: pwForm.current,
        newPassword:     pwForm.next,
        channel:         pwChannel,
      });
      setPwMasked(res.data.maskedDestination || (pwChannel === 'email' ? 'your email' : 'your phone'));
      setPwCode('');
      setPwStep('sent');
    } catch (err) {
      setPwError(err.message || 'Failed to send verification code. Try again.');
    } finally {
      setPwSending(false);
    }
  };

  const handleVerifyCode = async () => {
    setPwError('');
    if (!pwCode.trim()) return setPwError('Please enter the verification code.');
    setPwVerifying(true);
    try {
      await confirmPasswordChange({ accountId: user.accountId, code: pwCode.trim() });
      setPwStep('done');
    } catch (err) {
      setPwError(err.message || 'Verification failed. Try again.');
    } finally {
      setPwVerifying(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await updatePortalAccount({
        accountId: user.accountId,
        updates: {
          email: form.email,
          username: form.username,
          phone: form.phone,
          dateOfBirth: form.dateOfBirth || null,
          address: form.address,
          emergencyContact: form.emergencyContact,
        },
      });
      // Update parent state + localStorage so banner fields clear immediately
      onUserUpdate?.({
        email:            form.email,
        username:         form.username,
        phone:            form.phone,
        dateOfBirth:      form.dateOfBirth || null,
        address:          form.address,
        emergencyContact: form.emergencyContact,
      });
      setSaved(true);
      setDirty(false);
      onDirtyChange?.(false, null);
    } catch (err) {
      setError(err.message || 'Failed to save. Try again.');
      throw err; // re-throw so guard modal knows save failed
    } finally {
      setSaving(false);
    }
  };
  saveFnRef.current = handleSave;


  return (
    <div className="space-y-6 max-w-xl">

      {/* Read-only identity */}
      <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-5">
        <p className="text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-3">Identity</p>
        <div className="grid grid-cols-2 gap-3">
          <ReadOnly label="First Name" value={user.firstName} />
          <ReadOnly label="Last Name"  value={user.lastName} />
          <ReadOnly label="Role"       value={user.role} className="capitalize" />
          <ReadOnly label="Position"   value={user.position || '—'} />
        </div>
        <p className="text-sm text-neutral-400 mt-3">Name is permanent. Role and pay can be adjusted by an owner via the Employees tab.</p>
      </div>

      {/* Contact info */}
      <Section label="Contact Info">
        <Field label="Email" type="email" value={form.email} onChange={v => set('email', v)} />
        <Field label="Phone" type="tel" value={form.phone}
          onChange={v => set('phone', formatPhone(v))} placeholder="___-___-____" maxLength={12} />
        <Field label="Username" value={form.username} onChange={v => set('username', v)} />
        <Field label="Date of Birth" type="date" value={form.dateOfBirth} onChange={v => set('dateOfBirth', v)} />
      </Section>

      {/* Address */}
      <Section label="Address">
        <Field label="Street" value={form.address.street} onChange={v => setAddr('street', v)} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="City" value={form.address.city} onChange={v => setAddr('city', v)} />
          <div>
            <label className="block text-sm font-medium text-neutral-500 mb-1">State</label>
            <select
              value={form.address.state}
              onChange={e => setAddr('state', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none"
            >
              <option value="">—</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <Field label="ZIP Code" value={form.address.zip} onChange={v => setAddr('zip', v.replace(/\D/g, '').slice(0, 5))} placeholder="00000" />
      </Section>

      {/* Emergency Contact */}
      <Section label="Emergency Contact">
        <Field label="Name" value={form.emergencyContact.name} onChange={v => setEmerg('name', v)} />
        <Field label="Relationship" value={form.emergencyContact.relationship}
          onChange={v => setEmerg('relationship', v)} placeholder="e.g. Spouse, Parent" />
        <Field label="Phone" type="tel" value={form.emergencyContact.phone}
          onChange={v => setEmerg('phone', formatPhone(v))} placeholder="___-___-____" maxLength={12} />
      </Section>

      {/* Save button */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
        {saved && <span className="text-sm text-emerald-600 font-medium">Saved!</span>}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>

      {/* POS PIN */}
      <div>
        <p className="text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-3">POS PIN</p>
        <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4">
          {!pinEditing ? (
            <>
              <p className="text-sm text-neutral-500">
                Your 4-digit PIN is used to clock in and out at the POS terminal.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xl tracking-[0.3em] text-neutral-800">
                    {currentPin ? (pinRevealed ? currentPin : '••••') : '—'}
                  </span>
                  {currentPin && (
                    <button
                      onClick={() => setPinRevealed(r => !r)}
                      className="text-sm text-neutral-400 hover:text-neutral-700 transition-colors"
                    >
                      {pinRevealed ? 'Hide' : 'Show'}
                    </button>
                  )}
                </div>
                <button
                  onClick={() => { setPinEditing(true); setPinError(''); }}
                  className="px-4 py-1.5 text-sm font-medium border border-neutral-300 rounded-lg hover:border-neutral-500 transition-colors"
                >
                  {currentPin ? 'Change PIN' : 'Set PIN'}
                </button>
                {pinSaved && <span className="text-sm text-emerald-600 font-medium">PIN updated!</span>}
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-neutral-500 mb-1">New PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  pattern="\d{4}"
                  value={pinNew}
                  onChange={e => { setPinNew(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinError(''); }}
                  placeholder="4 digits"
                  maxLength={4}
                  className="w-32 px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300 tracking-widest text-center text-lg"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500 mb-1">Confirm PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  pattern="\d{4}"
                  value={pinConfirm}
                  onChange={e => { setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinError(''); }}
                  placeholder="4 digits"
                  maxLength={4}
                  className="w-32 px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300 tracking-widest text-center text-lg"
                />
              </div>
              {pinError && <p className="text-sm text-red-600">{pinError}</p>}
              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={handlePinSave}
                  disabled={pinSaving || pinNew.length < 4 || pinConfirm.length < 4}
                  className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
                >
                  {pinSaving ? 'Saving…' : 'Save PIN'}
                </button>
                <button onClick={pinReset} className="text-sm text-neutral-400 hover:text-neutral-600 transition-colors">
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Password change — multi-step with verification */}
      <div>
        <p className="text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-3">Change Password</p>

        {/* Step: idle */}
        {pwStep === 'idle' && (
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <p className="text-sm text-neutral-500 mb-4">
              A verification code will be sent to confirm your identity before any password change is applied.
            </p>
            <button
              onClick={() => { setPwError(''); setPwStep('enter'); }}
              className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 transition-colors"
            >
              Change Password
            </button>
          </div>
        )}

        {/* Step: enter — password fields + channel picker */}
        {pwStep === 'enter' && (
          <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4">
            <Field label="Current Password"     type="password" value={pwForm.current}  onChange={v => setPw('current', v)} />
            <Field label="New Password"         type="password" value={pwForm.next}     onChange={v => setPw('next', v)} />
            <Field label="Confirm New Password" type="password" value={pwForm.confirm}  onChange={v => setPw('confirm', v)} />

            {/* Channel picker */}
            <div>
              <p className="text-sm font-medium text-neutral-500 mb-2">Send verification code via</p>
              <div className="flex gap-3">
                {[
                  { id: 'email', label: 'Email',  detail: user.email  || 'No email on file',  disabled: !user.email  },
                  { id: 'sms',   label: 'Text',   detail: user.phone  || 'No phone on file',  disabled: !user.phone  },
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    disabled={opt.disabled}
                    onClick={() => setPwChannel(opt.id)}
                    className={`flex-1 text-left px-4 py-3 rounded-xl border text-sm transition-all ${
                      opt.disabled
                        ? 'border-neutral-200 opacity-40 cursor-not-allowed'
                        : pwChannel === opt.id
                          ? 'border-neutral-900 bg-neutral-900 text-white'
                          : 'border-neutral-300 text-neutral-700 hover:border-neutral-500'
                    }`}
                  >
                    <p className="font-semibold leading-tight">{opt.label}</p>
                    <p className={`text-xs mt-0.5 truncate ${pwChannel === opt.id ? 'text-neutral-300' : 'text-neutral-400'}`}>
                      {opt.detail}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {pwError && <p className="text-sm text-red-600">{pwError}</p>}

            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={handleSendCode}
                disabled={pwSending}
                className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
              >
                {pwSending ? 'Sending…' : 'Send Verification Code'}
              </button>
              <button onClick={pwReset} className="text-sm text-neutral-400 hover:text-neutral-600 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Step: sent — code entry */}
        {pwStep === 'sent' && (
          <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4">
            <div className="flex items-start gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
              <span className="shrink-0 mt-0.5">📨</span>
              <span>
                A 6-digit verification code was sent to <strong>{pwMasked}</strong>.
                Enter it below to confirm your password change.
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-500 mb-1">Verification Code</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={pwCode}
                onChange={e => { setPwCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setPwError(''); }}
                placeholder="000000"
                className="w-40 px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300 tracking-widest text-center font-mono"
                autoFocus
              />
            </div>

            {pwError && <p className="text-sm text-red-600">{pwError}</p>}

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <button
                onClick={handleVerifyCode}
                disabled={pwVerifying || pwCode.length < 6}
                className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
              >
                {pwVerifying ? 'Verifying…' : 'Verify & Change Password'}
              </button>
              <button
                onClick={() => { setPwStep('enter'); setPwCode(''); setPwError(''); }}
                disabled={pwSending}
                className="text-sm text-neutral-400 hover:text-neutral-600 transition-colors disabled:opacity-40"
              >
                {pwSending ? 'Sending…' : 'Resend code'}
              </button>
              <button onClick={pwReset} className="text-sm text-neutral-400 hover:text-neutral-600 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Step: done */}
        {pwStep === 'done' && (
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <div className="flex items-center gap-3 text-emerald-700">
              <span className="text-xl">✓</span>
              <p className="text-sm font-semibold">Password updated successfully.</p>
            </div>
            <button onClick={pwReset} className="mt-3 text-sm text-neutral-400 hover:text-neutral-600 transition-colors">
              Done
            </button>
          </div>
        )}
      </div>

      {/* Pay rate history — read-only, visible to all roles */}
      {user.primaryRestaurantId && user.employeeId && (
        <PayRateHistory restaurantId={user.primaryRestaurantId} employeeId={user.employeeId} />
      )}
    </div>
  );
};

// ─── Pay Rate History ─────────────────────────────────────────────────────────

const PayRateHistory = ({ restaurantId, employeeId }) => {
  const [rates, setRates] = useState(null);

  useEffect(() => {
    let active = true;
    getEmployeePayRates({ restaurantId, employeeId })
      .then(res => { if (active) setRates(res.data.rates || []); })
      .catch(() => { if (active) setRates([]); });
    return () => { active = false; };
  }, [restaurantId, employeeId]);

  const formatRate = (r) => {
    if (r.rateType === 'salary') return `$${Number(r.amount).toLocaleString()} / yr`;
    return `$${Number(r.amount).toFixed(2)} / hr`;
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };

  return (
    <div>
      <p className="text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-3">Pay Rate History</p>
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        {rates === null ? (
          <p className="text-sm text-neutral-400 text-center py-6">Loading…</p>
        ) : rates.length === 0 ? (
          <p className="text-sm text-neutral-400 text-center py-6">No pay rate history on file.</p>
        ) : (
          <div className="divide-y divide-neutral-100">
            {rates.map((r, i) => (
              <div key={r.id} className={`px-5 py-3 flex items-center justify-between ${i === 0 ? 'bg-emerald-50' : ''}`}>
                <div>
                  <p className="text-sm font-semibold text-neutral-800">{formatRate(r)}</p>
                  <p className="text-sm text-neutral-400 mt-0.5">
                    {formatDate(r.effectiveStart)}
                    {r.effectiveEnd ? ` → ${formatDate(r.effectiveEnd)}` : ' → present'}
                  </p>
                  {r.reason && <p className="text-sm text-neutral-400 italic mt-0.5">{r.reason}</p>}
                </div>
                {i === 0 && (
                  <span className="text-sm font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full shrink-0">
                    Current
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildForm(user) {
  return {
    email: user.email || '',
    username: user.username || '',
    phone: user.phone || '',
    dateOfBirth: user.dateOfBirth || '',
    address: {
      street: user.address?.street || '',
      city:   user.address?.city   || '',
      state:  user.address?.state  || '',
      zip:    user.address?.zip    || '',
    },
    emergencyContact: {
      name:         user.emergencyContact?.name         || '',
      relationship: user.emergencyContact?.relationship || '',
      phone:        user.emergencyContact?.phone        || '',
    },
  };
}

const Section = ({ label, children }) => (
  <div>
    <p className="text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-3">{label}</p>
    <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">{children}</div>
  </div>
);

const Field = ({ label, value, onChange, type = 'text', placeholder = '', maxLength }) => (
  <div>
    <label className="block text-sm font-medium text-neutral-500 mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
    />
  </div>
);

const ReadOnly = ({ label, value, className = '' }) => (
  <div>
    <p className="text-sm font-medium text-neutral-400 mb-0.5">{label}</p>
    <p className={`text-sm font-medium text-neutral-700 ${className}`}>{value}</p>
  </div>
);

export default AccountSettings;
